'use client'

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Property, User } from '@/types'
import { useCurrency } from '@/lib/currency'

interface PropertyWithLandlord extends Property {
  landlord: User
}

interface SimilarPropertiesProps {
  currentProperty: PropertyWithLandlord
}

export default function SimilarProperties({ currentProperty }: SimilarPropertiesProps) {
  const [similarProperties, setSimilarProperties] = useState<PropertyWithLandlord[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { format } = useCurrency()

  useEffect(() => {
    const loadSimilarProperties = async () => {
      if (!currentProperty) return

      try {
        setLoading(true)
        // Calculate price range (±30%)
        const priceRange = currentProperty.price * 0.3
        const minPrice = Math.floor(currentProperty.price - priceRange)
        const maxPrice = Math.ceil(currentProperty.price + priceRange)
        
        // Extract location (city) from current property
        const location = currentProperty.location?.split(',')[0] || ''
        
        // Build query parameters
        const queryParams = new URLSearchParams()
        queryParams.set('verified', 'true')
        queryParams.set('minPrice', minPrice.toString())
        queryParams.set('maxPrice', maxPrice.toString())
        if (location) queryParams.set('location', location)
        // Exclude current property
        if (currentProperty.id) queryParams.set('exclude', currentProperty.id)
        queryParams.set('limit', '5') // Limit to 5 similar properties
        
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
        const response = await fetch(`${apiUrl}/properties?${queryParams.toString()}`, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch similar properties: ${response.statusText}`)
        }

        const data = await response.json()
        // Transform backend format to frontend format
        const propertiesArray = Array.isArray(data) ? data : (data.properties || [])
        const transformedProperties = propertiesArray
          .filter((p: any) => (p._id || p.id) !== currentProperty.id) // Exclude current property
          .map((p: any) => ({
            ...p,
            id: p._id || p.id,
            landlord: p.landlord || currentProperty.landlord // Preserve landlord info
          }))
          .slice(0, 5) // Limit to 5 results
        
        setSimilarProperties(transformedProperties)
      } catch (error) {
        console.error('Error loading similar properties:', error)
        setSimilarProperties([])
      } finally {
        setLoading(false)
      }
    }

    loadSimilarProperties()
  }, [currentProperty])

  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  // Format duration
  const formatDuration = (months: number) => {
    if (months === 12) return '1 year'
    if (months === 6) return '6 months'
    if (months === 18) return '18 months'
    if (months === 24) return '2 years'
    return `${months} months`
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">Finding similar properties...</p>
      </div>
    )
  }

  if (similarProperties.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-gray-400 mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <p className="text-sm text-gray-500">No similar properties found</p>
        <p className="text-xs text-gray-400 mt-1">Try adjusting your search criteria</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Search Criteria Info */}
      <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border">
        <p className="font-medium mb-1">Similar Properties Criteria:</p>
        <p>📍 Location: {currentProperty.location.split(',')[0]}</p>
        <p>💰 Price: {formatPrice(Math.floor(currentProperty.price * 0.7))} - {formatPrice(Math.ceil(currentProperty.price * 1.3))}</p>
        <p>✅ Verified & Available properties only</p>
      </div>
      
      {similarProperties.map((property) => (
        <div 
          key={property.id} 
          className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
          onClick={() => navigate(`/property/${property.id}`)}
        >
          <div className="flex space-x-3">
            {/* Property Image */}
            <div className="flex-shrink-0 w-20 h-16 bg-gray-200 rounded overflow-hidden">
              {property.images && property.images.length > 0 ? (
                <img
                  src={property.images[0]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder-property.svg'
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-900 truncate hover:text-blue-600">
                {property.title}
              </h4>
              <p className="text-xs text-gray-600 truncate">{property.location}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-sm font-semibold text-green-600">
                  {formatPrice(property.price)}
                </p>
                <p className="text-xs text-gray-500">
                  {property.duration ? formatDuration(property.duration) : 'N/A'}
                </p>
              </div>
              <div className="flex items-center space-x-2 mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✓ Verified
                </span>
                {property.images && property.images.length > 0 && (
                  <span className="text-xs text-gray-500">
                    📸 {property.images.length} photo{property.images.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      
             {/* View All Similar Properties Button */}
       <div className="pt-2">
         <button
           onClick={() => {
             const location = currentProperty.location.split(',')[0] // Use first part of location
             const priceMin = Math.floor(currentProperty.price * 0.7)
             const priceMax = Math.ceil(currentProperty.price * 1.3)
             navigate(`/dashboard/client?location=${encodeURIComponent(location)}&price_min=${priceMin}&price_max=${priceMax}`)
           }}
           className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium py-2 border border-blue-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-colors"
         >
           View All Similar Properties →
         </button>
         <p className="text-xs text-gray-500 text-center mt-1">
           Based on location and price range (±30%)
         </p>
       </div>
    </div>
  )
}

